<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import asaasConfigAPI from 'dashboard/api/asaasConfig';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const isSaving = ref(false);
const configId = ref(null);
const apiKeySet = ref(false);
const form = ref({ api_key: '', environment: 'production', enabled: false });

const apiKeyPlaceholder = computed(() =>
  apiKeySet.value
    ? t('REVENDA.PROVIDER.API_KEY_SET')
    : t('REVENDA.PROVIDER.API_KEY_PLACEHOLDER')
);

const webhookUrl = computed(
  () => `${window.location.origin}/webhooks/asaas/reseller`
);

const copyWebhookUrl = () => {
  navigator.clipboard?.writeText(webhookUrl.value);
  useAlert(t('REVENDA.PROVIDER.WEBHOOK.COPIED'));
};

const loadConfig = async () => {
  try {
    const { data } = await asaasConfigAPI.get();
    configId.value = data.id || null;
    apiKeySet.value = Boolean(data.api_key_set);
    form.value.environment = data.environment || 'production';
    form.value.enabled = Boolean(data.enabled);
    form.value.api_key = '';
  } catch (error) {
    // sem config ainda — começa em branco
  }
};

onMounted(loadConfig);

const save = async () => {
  isSaving.value = true;
  try {
    const payload = {
      environment: form.value.environment,
      enabled: form.value.enabled,
    };
    if (form.value.api_key) payload.api_key = form.value.api_key;

    if (configId.value) {
      await asaasConfigAPI.update(payload);
    } else {
      await asaasConfigAPI.create(payload);
    }
    useAlert(t('REVENDA.PROVIDER.SAVED'));
    await loadConfig();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REVENDA.PROVIDER.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full gap-4 py-6">
    <div>
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('REVENDA.PROVIDER.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('REVENDA.PROVIDER.SUBTITLE') }}
      </p>
    </div>

    <div class="grid gap-6 lg:grid-cols-2">
      <!-- Configuração do provedor -->
      <div
        class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('REVENDA.PROVIDER.API_KEY') }}
          <input
            v-model="form.api_key"
            type="password"
            autocomplete="off"
            :placeholder="apiKeyPlaceholder"
            class="w-full"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('REVENDA.PROVIDER.ENVIRONMENT') }}
          <select v-model="form.environment" class="w-full">
            <option value="production">
              {{ t('REVENDA.PROVIDER.PRODUCTION') }}
            </option>
            <option value="sandbox">
              {{ t('REVENDA.PROVIDER.SANDBOX') }}
            </option>
          </select>
        </label>

        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.enabled" type="checkbox" />
          {{ t('REVENDA.PROVIDER.ENABLED') }}
        </label>

        <div class="flex justify-start">
          <Button
            :label="t('REVENDA.PROVIDER.SAVE')"
            :is-loading="isSaving"
            @click="save"
          />
        </div>
      </div>

      <!-- Webhook: o parceiro cola esta URL no Asaas dele para o status de
           pagamento das assinaturas dos clientes voltar ao painel. -->
      <div
        class="flex flex-col gap-2 p-5 border rounded-xl border-n-weak bg-n-solid-2 h-fit"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('REVENDA.PROVIDER.WEBHOOK.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('REVENDA.PROVIDER.WEBHOOK.HELP') }}
        </p>
        <div class="flex items-center gap-2 mt-1">
          <code
            class="flex-1 px-2 py-1.5 text-xs rounded bg-n-solid-1 text-n-slate-12 break-all"
          >
            {{ webhookUrl }}
          </code>
          <Button
            variant="ghost"
            size="sm"
            icon="i-lucide-copy"
            :label="t('REVENDA.PROVIDER.WEBHOOK.COPY')"
            @click="copyWebhookUrl"
          />
        </div>
      </div>
    </div>
  </div>
</template>
